import React, { useEffect } from "react";
import { Box, Table, Sheet, Typography, Button } from "@mui/joy";
import { toast } from "react-toastify";
import { LoadingView, PageTitle } from "../../../components";
import { formatDate, thousandSeparator } from "../../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { ArrowBack, WarningRounded } from "@mui/icons-material";
import { STATUS, getMerchantTransTypeLabel } from "../../../constant";
import {
  viewMerchantTransactionRequest,
  viewMerchantTransactionReset,
} from "../../../store/actions";

function CreateItems(title: string, value: string | number) {
  return { title, value };
}

const MerchantTransactionDetailsPage = ({
  loginResult,
  viewMerchantTransactionStatus,
  viewMerchantTransactionResult,
  viewMerchantTransactionErrorMessage,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const transaction_id = state?.transaction_id;

  useEffect(() => {
    if (transaction_id != null) {
      dispatch(
        viewMerchantTransactionRequest(loginResult.token, {
          id: transaction_id,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (viewMerchantTransactionStatus === STATUS.ERROR) {
      toast.error(viewMerchantTransactionErrorMessage);
      dispatch(viewMerchantTransactionReset());
    }
  }, [
    viewMerchantTransactionStatus,
    viewMerchantTransactionErrorMessage,
    dispatch,
  ]);

  const details = viewMerchantTransactionResult;

  const rows = details
    ? [
        CreateItems("Merchant Code", details.merchant_code),
        CreateItems(
          "Amount",
          `${thousandSeparator(details.amount)}${
            details.currency ? ` ${details.currency}` : ""
          }`,
        ),
        CreateItems("Currency", details.currency),
        CreateItems("Payer Name", details.payer_name),
        CreateItems("Customer Reference", details.customer_reference),
        CreateItems("OpenPesa Reference", details.openpesa_reference),
        CreateItems(
          "Status",
          details.status === "SUCCESS"
            ? "Success"
            : details.status === "FAILED"
              ? "Failed"
              : details.status,
        ),
        CreateItems(
          "Transaction Date",
          formatDate(details.trans_date),
        ),
        CreateItems(
          "Transaction Type",
          getMerchantTransTypeLabel(details.trans_type),
        ),
        CreateItems("Notification Date", formatDate(details.notification_date)),
        CreateItems("Description", details.description),
        CreateItems("FSP Transaction Ref", details.fsp_trans_ref),
        CreateItems("OP Status", details.op_status),
        CreateItems("Created At", formatDate(details.created_at)),
        CreateItems("Updated At", formatDate(details.updated_at)),
        CreateItems("Remark", details.remark),
        CreateItems("Attempts", details.attempts),
        CreateItems("Source Account FSP", details.source_account_fsp),
        CreateItems("Source Account", details.source_account),
        CreateItems("Source Account Name", details.source_account_name),
        CreateItems("Merchant Account FSP", details.merchant_account_fsp),
        CreateItems("Merchant Account", details.merchant_account),
        CreateItems("Merchant Account Name", details.merchant_account_name),
      ]
    : [];

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Box>
      <PageTitle title="Merchant Transaction Details" />

      <LoadingView loading={viewMerchantTransactionStatus === STATUS.LOADING} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Button
            color="neutral"
            variant="outlined"
            startDecorator={<ArrowBack />}
            onClick={goBack}
          >
            Back
          </Button>
        </Box>

        {details ? (
          <Box
            sx={{
              gap: 2,
              display: "flex",
              flexDirection: "column",
              width: { xs: "unset", md: "70%" },
            }}
          >
            <Sheet
              className="OrderTableContainer"
              variant="outlined"
              sx={{
                display: "flex",
                borderRadius: "sm",
                flexShrink: 1,
                overflow: "auto",
                minHeight: 0,
              }}
            >
              <Table
                aria-labelledby="tableTitle"
                stickyHeader
                hoverRow
                sx={{
                  "--TableCell-headBackground":
                    "var(--joy-palette-background-level2)",
                  "--Table-headerUnderlineThickness": "1px",
                  "--TableRow-hoverBackground":
                    "var(--joy-palette-background-level1)",
                  "--TableCell-paddingY": "4px",
                  "--TableCell-paddingX": "8px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "40%", fontSize: 18 }}>
                      Transaction Details
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <b>{row.title}</b>
                      </td>
                      <td>
                        <Typography
                          level="body-sm"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {row.value || "-"}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          </Box>
        ) : (
          viewMerchantTransactionStatus !== STATUS.LOADING && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                top: "10%",
              }}
            >
              <WarningRounded
                sx={{ fontSize: "50px", color: "background.appcolor" }}
              />
              <Typography level="title-md">Resource not Found</Typography>
              <Typography level="body-sm" textAlign="center">
                Transaction details could not be loaded or a network issue
                occurred.
              </Typography>
              <Button
                onClick={() =>
                  dispatch(
                    viewMerchantTransactionRequest(loginResult.token, {
                      id: transaction_id,
                    }),
                  )
                }
              >
                REFRESH
              </Button>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ merchant, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    viewMerchantTransactionStatus,
    viewMerchantTransactionResult,
    viewMerchantTransactionErrorMessage,
  } = merchant;
  return {
    loginResult,
    viewMerchantTransactionStatus,
    viewMerchantTransactionResult,
    viewMerchantTransactionErrorMessage,
  };
};

export default connect(
  mapStateToProps,
  {},
)(MerchantTransactionDetailsPage);
